import type {
  ChangelogSectionKey,
  ChangelogSections,
  ChangelogSignificance,
} from "@/features/changelog/api/changelog.api";
import { CHANGELOG_SECTION_ORDER } from "./changelog-display";

/**
 * Mirrors the bounds in `services/api/src/schemas/changelog.schemas.ts`. Kept in
 * step by hand rather than shared, because the API's copy is the one that
 * decides — this one only exists so an admin is told before they hit Save.
 */
export const CHANGELOG_LIMITS = {
  title: 120,
  headline: 200,
  topic: 80,
  description: 500,
  itemsPerSection: 25,
} as const;

/** Jira keys such as `AD-2075`; the API rejects anything else. */
export const TICKET_PATTERN = /^[A-Z][A-Z0-9]+-\d+$/;

/**
 * An item being edited. `uid` is local to the editing session: the API has no
 * item ids, and React keys cannot be the index while rows can be reordered.
 */
export interface EditableItem {
  uid: string;
  topic: string;
  description: string;
  ticket: string;
}

export type EditableSections = Record<ChangelogSectionKey, EditableItem[]>;

/** What the editor reads and writes. Both units it serves share this shape. */
export interface EditableSource {
  /**
   * Null for a deploy, which is named by its version tag. A roundup is named by
   * whoever composed it, so the title is part of what there is to edit.
   */
  title: string | null;
  headline: string | null;
  /**
   * Null for a roundup. A deploy carries the label the generator proposed —
   * big news or a small update — and correcting it is part of editing.
   */
  significance: ChangelogSignificance | null;
  sections: ChangelogSections;
}

export interface EditableDraft {
  title: string | null;
  headline: string;
  significance: ChangelogSignificance | null;
  sections: EditableSections;
}

let uidCounter = 0;

export function createItemUid(): string {
  uidCounter += 1;
  return `item-${uidCounter}`;
}

export function createEmptyItem(): EditableItem {
  return { uid: createItemUid(), topic: "", description: "", ticket: "" };
}

export function toEditableDraft(source: EditableSource): EditableDraft {
  return {
    title: source.title,
    headline: source.headline ?? "",
    significance: source.significance,
    sections: {
      new: toEditableItems(source.sections.new),
      improved: toEditableItems(source.sections.improved),
      fixed: toEditableItems(source.sections.fixed),
    },
  };
}

function toEditableItems(items: ChangelogSections[ChangelogSectionKey]): EditableItem[] {
  return items.map((item) => ({
    uid: createItemUid(),
    topic: item.topic,
    description: item.description,
    ticket: item.ticket ?? "",
  }));
}

/** The shape the API wants: trimmed, with an absent ticket as null. */
export function toRequestSections(draft: EditableDraft): ChangelogSections {
  return {
    new: toRequestItems(draft.sections.new),
    improved: toRequestItems(draft.sections.improved),
    fixed: toRequestItems(draft.sections.fixed),
  };
}

function toRequestItems(items: EditableItem[]) {
  return items.map((item) => ({
    topic: item.topic.trim(),
    description: item.description.trim(),
    ticket: item.ticket.trim() || null,
  }));
}

export function toRequestHeadline(draft: EditableDraft): string | null {
  return draft.headline.trim() || null;
}

/**
 * Compares what the API would receive, not what the form holds, so trailing
 * whitespace or a re-typed identical word does not arm the unsaved-changes
 * guard.
 */
export function isDraftDirty(source: EditableSource, draft: EditableDraft): boolean {
  if (draft.title !== null && draft.title.trim() !== source.title) return true;
  if (toRequestHeadline(draft) !== source.headline) return true;
  if (draft.significance !== source.significance) return true;

  const next = toRequestSections(draft);
  return CHANGELOG_SECTION_ORDER.some((key) => {
    const before = source.sections[key];
    const after = next[key];
    if (before.length !== after.length) return true;
    return after.some((item, index) => {
      const previous = before[index];
      return (
        previous === undefined ||
        previous.topic !== item.topic ||
        previous.description !== item.description ||
        previous.ticket !== item.ticket
      );
    });
  });
}

export type ItemField = "topic" | "description" | "ticket";

/**
 * An i18n key under `admin.changelog.editor.errors` plus whatever it
 * interpolates. Validation returns these rather than sentences so the editor
 * stays translatable, and so the limits live in one place instead of being
 * baked into English copy.
 */
export interface DraftError {
  key: string;
  params?: Record<string, number>;
}

/** Per-field errors keyed by item uid, so each input can carry its own. */
export type DraftErrors = Record<string, Partial<Record<ItemField, DraftError>>>;

export interface DraftValidation {
  errors: DraftErrors;
  /** Set when the whole draft is unsendable, rather than one field. */
  formError: DraftError | null;
  isValid: boolean;
}

/**
 * Rejects exactly what the API rejects. An empty draft is allowed here and
 * refused at approval instead: deleting every bullet is a legitimate way to say
 * a deploy shipped nothing user-facing.
 */
export function validateDraft(draft: EditableDraft): DraftValidation {
  const errors: DraftErrors = {};
  let formError: DraftError | null = null;

  if (draft.title !== null) {
    const title = draft.title.trim();
    if (title.length === 0) formError = { key: "titleRequired" };
    else if (title.length > CHANGELOG_LIMITS.title) {
      formError = { key: "titleTooLong", params: { max: CHANGELOG_LIMITS.title } };
    }
  }

  if (draft.headline.trim().length > CHANGELOG_LIMITS.headline) {
    formError = { key: "headlineTooLong", params: { max: CHANGELOG_LIMITS.headline } };
  }

  for (const key of CHANGELOG_SECTION_ORDER) {
    const items = draft.sections[key];

    if (items.length > CHANGELOG_LIMITS.itemsPerSection) {
      formError = {
        key: "tooManyItems",
        params: { max: CHANGELOG_LIMITS.itemsPerSection },
      };
    }

    for (const item of items) {
      const itemErrors: Partial<Record<ItemField, DraftError>> = {};
      const topic = item.topic.trim();
      const description = item.description.trim();
      const ticket = item.ticket.trim();

      if (topic.length === 0) itemErrors.topic = { key: "topicRequired" };
      else if (topic.length > CHANGELOG_LIMITS.topic) {
        itemErrors.topic = { key: "topicTooLong", params: { max: CHANGELOG_LIMITS.topic } };
      }

      if (description.length === 0) itemErrors.description = { key: "descriptionRequired" };
      else if (description.length > CHANGELOG_LIMITS.description) {
        itemErrors.description = {
          key: "descriptionTooLong",
          params: { max: CHANGELOG_LIMITS.description },
        };
      }

      if (ticket.length > 0 && !TICKET_PATTERN.test(ticket)) {
        itemErrors.ticket = { key: "ticketFormat" };
      }

      if (Object.keys(itemErrors).length > 0) errors[item.uid] = itemErrors;
    }
  }

  return {
    errors,
    formError,
    isValid: formError === null && Object.keys(errors).length === 0,
  };
}

/** Moves an item to a different section, appending it at the end. */
export function moveItemToSection(
  sections: EditableSections,
  uid: string,
  target: ChangelogSectionKey,
): EditableSections {
  const source = CHANGELOG_SECTION_ORDER.find((key) =>
    sections[key].some((item) => item.uid === uid),
  );
  if (source === undefined || source === target) return sections;

  const item = sections[source].find((candidate) => candidate.uid === uid);
  if (!item) return sections;

  return {
    ...sections,
    [source]: sections[source].filter((candidate) => candidate.uid !== uid),
    [target]: [...sections[target], item],
  };
}
